import http from '@/utils/request';
import type { PortalAccount } from '@/utils/auth';

/**
 * 登录业务码：
 * - APPROVED：登录成功
 * - PENDING / REJECTED：账号已注册但未通过审批
 * - NOT_REGISTERED：仅登录接口可能返回——该 openid 未注册，前端需引导至注册页
 */
export type WechatLoginStatus = 'APPROVED' | 'PENDING' | 'REJECTED' | 'NOT_REGISTERED';

/**
 * 微信登录账号最小信息（含 nickname，登录页友好展示用）
 */
export interface WechatLoginAccount {
  id: number;
  name: string | null;
  organization: string | null;
  nickname: string | null;
}

export interface WechatLoginResult {
  /** 业务状态：APPROVED → 登录成功；PENDING/REJECTED → 登录页提示，不写 token */
  status: WechatLoginStatus;
  /** 仅 APPROVED 时返回 */
  token?: string;
  /** APPROVED：登录账号；PENDING/REJECTED：用于展示昵称的最小信息 */
  account?: WechatLoginAccount;
}

export interface WechatAuthUrlResult {
  /** 拼好的微信开放平台扫码授权 URL */
  url: string;
  /** 后端透传/兜底生成的 state */
  state: string;
}

/** 申请微信扫码授权 URL（state 由前端生成做 CSRF 防护） */
export const fetchWechatAuthUrl = (state: string) =>
  http.get<unknown, WechatAuthUrlResult>('/portal/wechat/auth-url', {
    params: { state },
  });

/**
 * 微信登录：用 code 换 JWT / 状态（纯登录，不自动注册）。
 *
 * - 生产：微信开放平台扫码授权回调 query 中的 code
 * - 开发：`dev-<openid>`（需后端未配 WX_APPID/WX_APPSECRET）
 *
 * 当 openid 未注册时返回 status=NOT_REGISTERED，前端应引导去 /portal/register。
 */
export const wechatLogin = (code: string, state?: string) =>
  http.post<unknown, WechatLoginResult>('/portal/wechat/login', { code, state });

/**
 * 微信注册：用 code 自动落库（PENDING）或等同登录。
 *
 * 与 wechatLogin 的差异仅在"未注册时是否自动落库"：
 * - 未注册 → 自动插入 PENDING 账号，返回 PENDING
 * - 已注册 → 等同登录（APPROVED / PENDING / REJECTED）
 */
export const wechatRegister = (code: string, state?: string) =>
  http.post<unknown, WechatLoginResult>('/portal/wechat/register', { code, state });

/** 当前门户用户信息（中间件已用 openid 反查 t_account） */
export const fetchPortalProfile = () => http.get<unknown, PortalAccount>('/portal/me');

/** 静默探测门户登录态（仅用于登录页冷启动判断，失败不弹 toast、不跳转） */
export const probePortalProfile = () =>
  http.get<unknown, PortalAccount>('/portal/me', { silent: true });
